import { Link, LinkProps } from "react-router-dom";
import { useTable, useSortBy, Column } from "react-table";
import Loading from "../../../Loading/Loading";
import "./Table.css";
import { isPreregisteredStudent } from "./TableSortAndFormatFunctions";

interface TableProps<D extends object = {}> {
  kind: "student" | "assignment";
  columns: Array<Column<D>>;
  data: Array<object>;
  createRowLinkURL?: (row: any) => string;
}

const isStudent = (kind: string, row: any) => kind === "student" && isPreregisteredStudent(row);

const RowLink = ({ to, children, ...props }: LinkProps & { to: string }) =>
  to.includes("teacher/#") ? (
    <a href={to} {...props}>
      {children}
    </a>
  ) : (
    <Link to={to} {...props}>
      {children}
    </Link>
  );

const Table = (props: TableProps) => {
  const { columns, data } = props;

  const {
    getTableProps, // table props from react-table
    getTableBodyProps, // table body props from react-table
    headerGroups, // column headers
    rows, // rows for the table based on the data passed
    prepareRow, // Prepare the row (this function needs to be called for each row before getting the row props)
  } = useTable(
    {
      columns,
      data,
      initialState:
        data.length === 1
          ? {}
          : {
              sortBy: [
                {
                  // table starts with the students or assignments column sorted
                  id: props.kind === "student" ? "Students" : "Last Modified",
                  // will start ascending for students and descending for assignments
                  desc: props.kind === "student" ? false : true,
                },
              ],
            },
      // sorted state will automatically reset, when data changes
      // row order will update, but which/how column is sorted will not
      autoResetSortBy: false,
      // the un-sorted state will not be available to columns
      disableSortRemove: true,
    },
    useSortBy,
  );

  if (data.length === 0) {
    return <Loading />;
  }

  return (
    <div className="TableContainer">
      <table className={props.kind === "student" ? "Table Student" : "Table"} {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={
                    column.isSorted
                      ? `sortArrow ${column.isSortedDesc ? "descending" : "ascending"}`
                      : column.id === "optionsColumn" || rows.length === 1
                      ? "noSort"
                      : ""
                  }
                >
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                className={isStudent(props.kind, row) || !props.createRowLinkURL ? "" : "link"}
              >
                {row.cells.map(cell =>
                  isStudent(props.kind, row) || !props.createRowLinkURL ? (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ) : (
                    <td {...cell.getCellProps()}>
                      <RowLink to={props.createRowLinkURL(row)}>{cell.render("Cell")}</RowLink>
                    </td>
                  ),
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
